import {GitHubAvatar} from '@github-ui/github-avatar'
import {IssueFieldSingleSelectValueToken} from '@github-ui/issue-metadata/IssueFieldSingleSelectValueToken'
import {IssueTypeToken} from '@github-ui/issue-type-token'
import {useAnalytics} from '@github-ui/use-analytics'
import {CheckIcon, ChevronRightIcon, InfoIcon} from '@primer/octicons-react'
import {AnchoredOverlay, Button, IconButton, IssueLabelToken, Label, Link, RelativeTime} from '@primer/react'
import {clsx} from 'clsx'
import {memo, useCallback, useEffect, useRef, useState} from 'react'

import type {useRecentAgentActivity_issue$key} from './__generated__/useRecentAgentActivity_issue.graphql'
import styles from './RecentAgentActivity.module.css'
import {useDismissRecentAgentActivity} from './use-dismiss-recent-agent-activity'
import type {AgentAction, LabelInfo} from './use-recent-agent-activity'
import {useRecentAgentActivity} from './use-recent-agent-activity'

type ActionRationaleProps = {
  rationale: string
  fieldName: string
}

function ActionRationale({rationale, fieldName}: ActionRationaleProps) {
  const [open, setOpen] = useState(false)
  const {sendAnalyticsEvent} = useAnalytics()

  const handleOpen = useCallback(() => {
    setOpen(true)
    sendAnalyticsEvent('recent_agent_actions_panel.rationale_clicked', 'RECENT_AGENT_ACTIVITY', {
      rationaleLength: rationale.length,
    })
  }, [sendAnalyticsEvent, rationale.length])

  const handleClose = useCallback(() => setOpen(false), [])

  return (
    <AnchoredOverlay
      open={open}
      onOpen={handleOpen}
      onClose={handleClose}
      renderAnchor={props => (
        // eslint-disable-next-line primer-react/a11y-remove-disable-tooltip
        <IconButton
          {...props}
          icon={InfoIcon}
          variant="invisible"
          size="small"
          aria-label={`View rationale for ${fieldName}`}
          unsafeDisableTooltip
          className={styles.rationaleIcon}
        />
      )}
    >
      <div className={styles.rationaleOverlay}>{rationale}</div>
    </AnchoredOverlay>
  )
}

type ActionRowProps = {
  action: AgentAction
  repoNameWithOwner: string
}

const LabelTokenWithRationale = memo(function LabelTokenWithRationale({
  label,
  repoNameWithOwner,
}: {
  label: LabelInfo
  repoNameWithOwner: string
}) {
  return (
    <>
      <Link href={`/${repoNameWithOwner}/issues?q=${encodeURIComponent(`label:"${label.name}"`)}`}>
        <IssueLabelToken
          text={label.name}
          fillColor={label.color ? `#${label.color}` : undefined}
          className={styles.clickableToken}
        />
      </Link>
      {label.rationale && <ActionRationale rationale={label.rationale} fieldName={label.name} />}
    </>
  )
})

function ActionValue({action, repoNameWithOwner}: ActionRowProps) {
  if (action.labels && action.labels.length > 0) {
    return (
      <span className={styles.labelTokens}>
        {action.labels.map(label => (
          <LabelTokenWithRationale key={label.name} label={label} repoNameWithOwner={repoNameWithOwner} />
        ))}
      </span>
    )
  }

  if (action.type === 'type_set' && action.color && action.value) {
    return (
      <>
        <IssueTypeToken
          name={action.value}
          color={action.color}
          href={`/${repoNameWithOwner}/issues?q=${encodeURIComponent(`type:"${action.value}"`)}`}
          getTooltipText={() => undefined}
        />
        {action.rationale && <ActionRationale rationale={action.rationale} fieldName={action.fieldName} />}
      </>
    )
  }

  if (action.type === 'field_set' && action.value) {
    const fieldSearchLink = `/${repoNameWithOwner}/issues?q=${encodeURIComponent(`field.${action.fieldName.replaceAll(' ', '-')}:"${action.value}"`)}`
    if (action.color) {
      return (
        <>
          <Link href={fieldSearchLink}>
            <IssueFieldSingleSelectValueToken
              name={action.value}
              color={action.color}
              getTooltipText={() => undefined}
              tokenClassName={styles.clickableToken}
            />
          </Link>
          {action.rationale && <ActionRationale rationale={action.rationale} fieldName={action.fieldName} />}
        </>
      )
    }
    return (
      <>
        <Link href={fieldSearchLink} inline>
          {action.value}
        </Link>
        {action.rationale && <ActionRationale rationale={action.rationale} fieldName={action.fieldName} />}
      </>
    )
  }

  return <span className={styles.fieldValue}>{action.value ?? ''}</span>
}

const ActionRow = memo(function ActionRow({action, repoNameWithOwner}: ActionRowProps) {
  return (
    <div className={styles.actionRow} role="listitem">
      <CheckIcon size={16} className={styles.checkIcon} aria-hidden="true" />
      <span className={styles.fieldName}>{action.fieldName}</span>
      <span className={styles.arrow} aria-hidden="true">
        →
      </span>
      <ActionValue action={action} repoNameWithOwner={repoNameWithOwner} />
      <span className={styles.timestamp}>
        <RelativeTime date={new Date(action.createdAt)} />
      </span>
    </div>
  )
})

type RecentAgentActivityProps = {
  issue: useRecentAgentActivity_issue$key
}

const FEEDBACK_URL = 'https://github-grid.enterprise.slack.com/archives/C0ASY8L0CQ3'

export function RecentAgentActivity({issue}: RecentAgentActivityProps) {
  const {hasRecentActivity, groups, totalActionCount, viewerCanUpdateMetadata, issueId, repoNameWithOwner} =
    useRecentAgentActivity(issue)
  const {isDismissed, dismissForIssue, dismissForever} = useDismissRecentAgentActivity(issueId)
  const [expanded, setExpanded] = useState(false)
  const {sendAnalyticsEvent} = useAnalytics()

  const handleToggle = useCallback(() => {
    const next = !expanded
    setExpanded(next)
    sendAnalyticsEvent(
      next ? 'recent_agent_actions_panel.expanded' : 'recent_agent_actions_panel.minimized',
      'RECENT_AGENT_ACTIVITY',
      {
        action_count: totalActionCount,
        agent_count: groups.length,
      },
    )
  }, [expanded, sendAnalyticsEvent, totalActionCount, groups.length])

  const hasFiredDisplayedRef = useRef(false)
  useEffect(() => {
    if (hasRecentActivity && viewerCanUpdateMetadata && !isDismissed && !hasFiredDisplayedRef.current) {
      hasFiredDisplayedRef.current = true
      sendAnalyticsEvent('recent_agent_actions_panel.displayed', 'RECENT_AGENT_ACTIVITY', {
        action_count: totalActionCount,
        agent_count: groups.length,
      })
    }
  }, [hasRecentActivity, viewerCanUpdateMetadata, isDismissed, sendAnalyticsEvent, totalActionCount, groups.length])

  if (!hasRecentActivity || !viewerCanUpdateMetadata || isDismissed) {
    return null
  }

  const primaryGroup = groups[0]
  if (!primaryGroup) return null

  return (
    <div className={styles.container} data-testid="recent-agent-activity">
      <button
        type="button"
        className={styles.trigger}
        onClick={handleToggle}
        aria-expanded={expanded}
        aria-controls="recent-agent-activity-content"
      >
        <span className={styles.label}>Recent agent activity</span>
        <span className={styles.actorInfo}>
          <GitHubAvatar src={primaryGroup.actorAvatarUrl} size={20} alt="" />
          <span className={styles.actorName}>{primaryGroup.agentName}</span>
          <Label variant="secondary">AI</Label>
          <span className={styles.count}>{totalActionCount} applied</span>
        </span>
        <ChevronRightIcon
          size={16}
          className={clsx(styles.chevron, expanded && styles.chevronExpanded)}
          aria-hidden="true"
        />
      </button>

      {expanded && (
        <div id="recent-agent-activity-content" className={styles.expandedContent}>
          <div className={styles.content} role="list" aria-label="Agent actions">
            {groups.map(group =>
              group.actions.map(action => (
                <ActionRow
                  key={`${group.agentSlug}-${action.fieldName}-${action.value}-${action.createdAt}`}
                  action={action}
                  repoNameWithOwner={repoNameWithOwner}
                />
              )),
            )}
          </div>
          <div className={styles.footer}>
            <span className={styles.footerLinks}>
              <Label variant="success">Private preview</Label>
              <Link href={FEEDBACK_URL} target="_blank" rel="noopener noreferrer" inline>
                Give feedback
              </Link>
            </span>
            <span className={styles.footerActions}>
              <Button variant="invisible" size="small" onClick={dismissForIssue}>
                Dismiss
              </Button>
              <Button variant="invisible" size="small" onClick={dismissForever}>
                Never show this again
              </Button>
            </span>
          </div>
        </div>
      )}
    </div>
  )
}
